import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowUpRight, AlertCircle } from "lucide-react";

const RELEASE_FREQUENCIES = ["monthly", "weekly", "quarterly", "other"];

export const RequestStep = ({
  t, form, onChange, accountTypeError, onAccountTypeChange,
  releaseFrequencyError, onReleaseFrequencyChange, requestSending, requestError,
  requestRateLimited, onSubmit,
}) => (
  <>
    <DialogHeader className="p-8 pb-2 text-left">
      <DialogTitle className="font-heading text-2xl font-bold text-white">{t.modal.title}</DialogTitle>
      <DialogDescription className="text-neutral-400 text-sm leading-relaxed">{t.modal.description}</DialogDescription>
    </DialogHeader>

    <form onSubmit={onSubmit} data-lenis-prevent className="max-h-[70vh] overflow-y-auto p-8 pt-4 flex flex-col gap-5">
      <p className="text-brand-red text-xs font-bold uppercase tracking-wide -mb-2">{t.modal.sectionContact}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-name" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldName}</Label>
        <Input
          id="reg-name" data-testid="register-input-name" required maxLength={80}
          value={form.contactName} onChange={onChange("contactName")}
          placeholder={t.modal.placeholderName}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 h-11 rounded-lg focus-visible:ring-white/30"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-email" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldEmail}</Label>
        <Input
          id="reg-email" type="email" data-testid="register-input-email" required
          value={form.email} onChange={onChange("email")}
          placeholder={t.modal.placeholderEmail}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 h-11 rounded-lg focus-visible:ring-white/30"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-phone" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldPhone}</Label>
        <Input
          id="reg-phone" type="tel" data-testid="register-input-phone" required maxLength={40}
          value={form.phone} onChange={onChange("phone")}
          placeholder={t.modal.placeholderPhone}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 h-11 rounded-lg focus-visible:ring-white/30"
        />
      </div>

      <p className="text-brand-red text-xs font-bold uppercase tracking-wide -mb-2 mt-2">{t.modal.sectionArtist}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-label" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldLabel}</Label>
        <Input
          id="reg-label" data-testid="register-input-label" required maxLength={80}
          value={form.artistOrLabel} onChange={onChange("artistOrLabel")}
          placeholder={t.modal.placeholderLabel}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 h-11 rounded-lg focus-visible:ring-white/30"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldAccountType}</Label>
        <Select required value={form.accountType} onValueChange={onAccountTypeChange}>
          <SelectTrigger
            data-testid="register-select-account-type"
            className={`bg-white/[0.04] border-white/10 text-white h-11 rounded-lg focus:ring-brand-blue/40 ${accountTypeError ? "border-brand-red/70" : ""}`}
          >
            <SelectValue placeholder={t.modal.placeholderSelect} />
          </SelectTrigger>
          <SelectContent className="bg-[#141415] border-white/10 text-white">
            <SelectItem data-testid="register-account-type-artista" value="artista">{t.modal.optionArtist}</SelectItem>
            <SelectItem data-testid="register-account-type-label" value="label">{t.modal.optionLabel}</SelectItem>
            <SelectItem data-testid="register-account-type-manager" value="representante">{t.modal.optionManager}</SelectItem>
          </SelectContent>
        </Select>
        {accountTypeError && (
          <p data-testid="register-account-type-error" className="text-brand-red text-xs">{t.modal.accountTypeError}</p>
        )}
      </div>

      <p className="text-brand-red text-xs font-bold uppercase tracking-wide -mb-2 mt-2">{t.modal.sectionCatalog}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-catalog" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldCatalogSize}</Label>
        <Input
          id="reg-catalog" data-testid="register-input-catalog-size" required maxLength={200}
          value={form.catalogSize} onChange={onChange("catalogSize")}
          placeholder={t.modal.placeholderCatalogSize}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 h-11 rounded-lg focus-visible:ring-white/30"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldReleaseFrequency}</Label>
        <Select required value={form.releaseFrequency} onValueChange={onReleaseFrequencyChange}>
          <SelectTrigger
            data-testid="register-select-release-frequency"
            className={`bg-white/[0.04] border-white/10 text-white h-11 rounded-lg focus:ring-brand-blue/40 ${releaseFrequencyError ? "border-brand-red/70" : ""}`}
          >
            <SelectValue placeholder={t.modal.placeholderReleaseFrequency} />
          </SelectTrigger>
          <SelectContent className="bg-[#141415] border-white/10 text-white">
            {RELEASE_FREQUENCIES.map((freq) => (
              <SelectItem key={freq} data-testid={`register-release-frequency-${freq}`} value={freq}>
                {t.modal[`releaseFrequency${freq.charAt(0).toUpperCase()}${freq.slice(1)}`]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {releaseFrequencyError && (
          <p data-testid="register-release-frequency-error" className="text-brand-red text-xs">{t.modal.releaseFrequencyError}</p>
        )}
      </div>

      <p className="text-brand-red text-xs font-bold uppercase tracking-wide -mb-2 mt-2">{t.modal.sectionExperience}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-experience" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldExperience}</Label>
        <Textarea
          id="reg-experience" data-testid="register-textarea-experience" required maxLength={1000} rows={3}
          value={form.experience} onChange={onChange("experience")}
          placeholder={t.modal.placeholderExperience}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 rounded-lg focus-visible:ring-white/30 resize-none"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-goals" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldGoals}</Label>
        <Textarea
          id="reg-goals" data-testid="register-textarea-goals" required maxLength={1000} rows={3}
          value={form.goals} onChange={onChange("goals")}
          placeholder={t.modal.placeholderGoals}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 rounded-lg focus-visible:ring-white/30 resize-none"
        />
      </div>

      <p className="text-brand-red text-xs font-bold uppercase tracking-wide -mb-2 mt-2">{t.modal.sectionLinks}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="reg-links" className="text-neutral-300 text-xs font-semibold tracking-wide uppercase">{t.modal.fieldSocialLinks}</Label>
        <Textarea
          id="reg-links" data-testid="register-textarea-social-links" required maxLength={1000} rows={2}
          value={form.socialLinks} onChange={onChange("socialLinks")}
          placeholder={t.modal.placeholderSocialLinks}
          className="bg-white/[0.04] border-white/10 text-white placeholder:text-neutral-500 rounded-lg focus-visible:ring-white/30 resize-none"
        />
      </div>

      {requestError && (
        <div data-testid="register-request-error" className="flex items-start gap-2.5 rounded-xl border border-brand-red/30 bg-brand-red/[0.06] p-3">
          <AlertCircle className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
          <p className="text-neutral-300 text-xs leading-relaxed">{requestRateLimited ? t.modal.rateLimitError : t.modal.requestSubmitError}</p>
        </div>
      )}

      <button
        type="submit"
        data-testid="register-continue-button"
        disabled={requestSending}
        className="mt-2 w-full bg-brand-red text-white font-bold text-sm py-3 rounded-full flex items-center justify-center gap-2 hover:-translate-y-0.5 hover:bg-brand-redDark shadow-[0_8px_24px_-6px_rgba(255,36,54,0.5)] transition-all duration-300 disabled:opacity-40 disabled:pointer-events-none"
      >
        {requestSending ? t.modal.sending : t.modal.continueButton}
        {!requestSending && <ArrowUpRight className="w-4 h-4" />}
      </button>
      <p className="text-[11px] text-neutral-500 text-center leading-relaxed">{t.modal.footnote}</p>
    </form>
  </>
);
